import { useCallback, useEffect, useState } from 'react';
import { isStandalone } from './useServiceWorker';

/**
 * The install prompt.
 *
 * Two rules from PWA_IMPLEMENTATION_PLAN §4, and both are about restraint:
 *
 *  - **Not on first paint.** Someone who has not yet decided whether they like the
 *    application is not deciding whether to install it. The banner appears from the
 *    THIRD visit onward.
 *  - **Asked once.** A dismissal is remembered, and the prompt never returns on its
 *    own. `/settings` keeps a manual install button, so the capability is never lost —
 *    it just stops interrupting.
 *
 * `localStorage` is the right place for both counters: they are cosmetic preferences,
 * they are per-device by nature, and nothing here is sensitive.
 */

const VISITS_KEY = 'bemedic.visits';
const DISMISSED_KEY = 'bemedic.installDismissed';
const MIN_VISITS = 3;

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A private window. The prompt then simply asks again next time, which is a far
    // smaller problem than failing to render.
  }
}

/** Counts this visit. Called once, from `main.tsx`. */
export function countVisit(): void {
  const visits = Number(read(VISITS_KEY) ?? '0') + 1;
  write(VISITS_KEY, String(visits));
}

export function useInstallPrompt() {
  const [available, setAvailable] = useState(deferred !== null);
  const [dismissed, setDismissed] = useState(read(DISMISSED_KEY) === 'true');

  useEffect(() => {
    const onPrompt = (event: Event): void => {
      // Chrome fires this when it decides the app is installable. Preventing the
      // default stops its own mini-infobar so the app can choose the moment.
      event.preventDefault();
      deferred = event as InstallPromptEvent;
      setAvailable(true);
    };

    const onInstalled = (): void => {
      deferred = null;
      setAvailable(false);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Declared before `install`, which calls it: a `useCallback` reading a const declared
  // below it is a temporal-dead-zone access, not merely untidy.
  const dismiss = useCallback(() => {
    write(DISMISSED_KEY, 'true');
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;

    await deferred.prompt();
    const { outcome } = await deferred.userChoice;

    // The event can only be used once, whatever the answer.
    deferred = null;
    setAvailable(false);

    // A dismissed install is an answer: it is not asked again on its own.
    if (outcome === 'dismissed') dismiss();
    return outcome;
  }, [dismiss]);

  const visits = Number(read(VISITS_KEY) ?? '0');

  return {
    /** Can the browser install this app at all? */
    available,
    dismissed,
    /** Should the banner be shown right now? */
    shouldOffer: available && !dismissed && visits >= MIN_VISITS && !isStandalone(),
    /**
     * iOS has no `beforeinstallprompt`; installing is Share → Add to Home Screen, and
     * the only honest thing to do is say so.
     */
    needsManualInstructions:
      !available && !isStandalone() && /iphone|ipad|ipod/i.test(navigator.userAgent),
    install,
    dismiss,
  };
}
